import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  HiOutlineCheckCircle,
  HiOutlineArrowPath,
  HiOutlineXMark,
  HiOutlineRocketLaunch,
} from "react-icons/hi2";
import { authService } from "../services";
import "./TenantOnboardingGuide.css";

const STORAGE_KEY_PREFIX = "max_onboarding_guide_dismissed_";

function getDismissed(tenantId) {
  if (!tenantId) return false;
  try {
    return localStorage.getItem(STORAGE_KEY_PREFIX + tenantId) === "1";
  } catch {
    return false;
  }
}

function setDismissed(tenantId) {
  if (!tenantId) return;
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + tenantId, "1");
  } catch {
    /* ignore */
  }
}

/**
 * Guided checklist for new tenants — masters to configure before day-to-day use.
 * Steps follow visible nav (subscription + feature apps).
 */
function TenantOnboardingGuide({
  guideLoading,
  steps,
  allDone,
  percentComplete,
  completedCount,
  totalCount,
  refetch,
}) {
  const tenantId = authService.getTenantId();
  const [dismissed, setDismissedState] = useState(() =>
    getDismissed(tenantId),
  );

  useEffect(() => {
    setDismissedState(getDismissed(tenantId));
  }, [tenantId]);

  /** Reserve space while nav + first checklist load (avoids empty-then-pop layout jump). */
  const showSkeleton = useMemo(
    () => !dismissed && guideLoading && totalCount === 0,
    [dismissed, guideLoading, totalCount],
  );

  const show = useMemo(() => {
    if (dismissed) return false;
    if (guideLoading && totalCount === 0) return false;
    if (totalCount === 0) return false;
    return true;
  }, [dismissed, guideLoading, totalCount]);

  if (showSkeleton) {
    return (
      <section
        className="tenant-onboarding tenant-onboarding--skeleton"
        aria-busy="true"
        aria-label="Loading setup checklist"
      >
        <div className="tenant-onboarding__skeleton-head" />
        <div className="tenant-onboarding__skeleton-bar" />
        <div className="tenant-onboarding__skeleton-rows">
          <div className="tenant-onboarding__skeleton-row" />
          <div className="tenant-onboarding__skeleton-row" />
          <div className="tenant-onboarding__skeleton-row short" />
        </div>
      </section>
    );
  }

  if (!show) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(tenantId);
    setDismissedState(true);
  };

  if (allDone) {
    return (
      <section className="tenant-onboarding tenant-onboarding--success" aria-label="Setup complete">
        <div className="tenant-onboarding__inner">
          <HiOutlineCheckCircle className="tenant-onboarding__icon" aria-hidden />
          <div className="tenant-onboarding__body">
            <h3 className="tenant-onboarding__title">You&apos;re ready to go</h3>
            <p className="tenant-onboarding__text">
              Your setup checklist is complete for the modules you use. Open{" "}
              <Link to="/home/apps">All modules</Link> anytime to explore more.
            </p>
          </div>
          <button
            type="button"
            className="tenant-onboarding__dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            <HiOutlineXMark aria-hidden />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="tenant-onboarding" aria-label="Getting started checklist">
      <div className="tenant-onboarding__head">
        <HiOutlineRocketLaunch className="tenant-onboarding__rocket" aria-hidden />
        <div className="tenant-onboarding__head-text">
          <h3 className="tenant-onboarding__title">Get started — configure your masters</h3>
          <p className="tenant-onboarding__lead">
            Complete these steps in order for your subscription and assigned apps. Settings
            (below in the app) stay available throughout. Only pages you can access are listed.
          </p>
        </div>
        <div className="tenant-onboarding__head-actions">
          <button
            type="button"
            className="tenant-onboarding__refresh"
            onClick={() => refetch?.()}
            title="Refresh progress"
          >
            <HiOutlineArrowPath aria-hidden />
            Refresh
          </button>
          <button
            type="button"
            className="tenant-onboarding__dismiss"
            onClick={handleDismiss}
            aria-label="Hide checklist"
            title="Hide until you clear browser data"
          >
            <HiOutlineXMark aria-hidden />
          </button>
        </div>
      </div>

      <div className="tenant-onboarding__progress-wrap">
        <div
          className="tenant-onboarding__progress-bar"
          role="progressbar"
          aria-valuenow={percentComplete}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="tenant-onboarding__progress-fill"
            style={{ width: `${percentComplete}%` }}
          />
        </div>
        <span className="tenant-onboarding__progress-label">
          {completedCount} / {totalCount} complete
        </span>
      </div>

      <ol className="tenant-onboarding__list">
        {steps.map((step, idx) => (
          <li
            key={step.id}
            className={`tenant-onboarding__item ${step.done ? "is-done" : ""}`}
          >
            <span className="tenant-onboarding__step-num">{idx + 1}</span>
            <div className="tenant-onboarding__item-body">
              <div className="tenant-onboarding__item-title-row">
                {step.done ? (
                  <HiOutlineCheckCircle
                    className="tenant-onboarding__check"
                    aria-hidden
                  />
                ) : (
                  <span className="tenant-onboarding__dot" aria-hidden />
                )}
                <strong>{step.title}</strong>
              </div>
              <p className="tenant-onboarding__desc">{step.description}</p>
              {!step.done && (
                <Link to={step.path} className="tenant-onboarding__link">
                  Open {step.label} →
                </Link>
              )}
            </div>
          </li>
        ))}
      </ol>

      <p className="tenant-onboarding__footer">
        Tip: Use <Link to="/home/apps">All modules</Link> to browse everything your tenant can
        use, or press <kbd className="tenant-onboarding__kbd">⌘K</kbd> /{" "}
        <kbd className="tenant-onboarding__kbd">Ctrl+K</kbd> to search.
      </p>
    </section>
  );
}

export default React.memo(TenantOnboardingGuide);
